import { TDropdownItem, TInputTypes, TLiftedFormModel } from '../../model/form.model';
import { PIPELINE_FORM } from '../../model/form.pipeline';
import { FORM_MODIFIER } from '../../model/form.modifier';
import {
  GET_SPAREPART_REQUEST_REASONS,
  TREQUEST_RAISE_SPAREPART_REQUEST,
  TRESPONSE_GET_ALL_BRANDS_AND_SPECIFICATIONS,
} from '@monorepo/types';

export type TSPForm = {
  form: TLiftedFormModel;
  isMinimized: boolean;
  currSpBrandsWithSpecifications: TRESPONSE_GET_ALL_BRANDS_AND_SPECIFICATIONS['brandsWithSpecifications'];
  reqSpBrandsAndSpecifications: TRESPONSE_GET_ALL_BRANDS_AND_SPECIFICATIONS['brandsWithSpecifications'];
};

export const SPAREPART_REQUEST_MODAL_KEYS = {
  CURRENT_NAME: 'current_name',
  CURRENT_SPECIFICATION: 'current_specification',
  CURRENT_BRAND: 'current_brand',
  SAME_AS_CURRENT: 'same_as_current',
  REQUESTED_NAME: 'requested_name',
  REQUESTED_SPECIFICATION: 'requested_specification',
  REQUESTED_BRAND: 'requested_brand',
  REASON: 'reason',
  QUANTITY: 'quantity',
  BROKEN_QUANTITY: 'broken_quantity',
  REQUESTED_IN_STOCK_QUANTITY: 'requested_in_stock_quantity',
  SUB_REASON: 'subReasonOpt',
  ADD_INFO: 'info',
} as const;

export type TSP_REQUEST_FIELD_KEYS = (typeof SPAREPART_REQUEST_MODAL_KEYS)[keyof typeof SPAREPART_REQUEST_MODAL_KEYS];

export type TSP_REQUEST_FORM_PAYLOAD = {
  current_name: string;
  current_specification?: string[];
  current_brand: string[];
  requested_name: string;
  requested_specification?: string[];
  requested_brand: string[];
  quantity: number;
  broken_quantity?: number;
  requested_in_stock_quantity?: number;
  same_as_current?: boolean;
};

export const RAISE_SPAREPART_REQUEST_FORM_MODEL = {
  createReasonForm: (
    reasonMap: GET_SPAREPART_REQUEST_REASONS,
    showSubReason: boolean,
    isDisabled?: boolean,
  ): TLiftedFormModel => {
    return PIPELINE_FORM.sanitize({
      inputs: [
        {
          input: {
            type: 'DROPDOWN',
            options: reasonMap.reasonList.map((x) => {
              return { value: x.reason, displayName: x.displayName };
            }),
          },
          metadata: {
            keyName: 'reason',
            label: 'tm_raise_request.RequestReason',
            isDisabled: isDisabled,
            isRequired: true,
          },
        },
        ...(showSubReason
          ? [
              {
                input: {
                  type: 'DROPDOWN' as const,
                  options: [],
                },
                metadata: {
                  keyName: 'subReasonOpt',
                  label: 'tm_raise_request.BrokenReason',
                  isDisabled: isDisabled,
                },
              },
            ]
          : []),
        {
          input: {
            type: 'TEXTAREA',
          },
          metadata: {
            keyName: 'info',
            label: 'raise_needle_request.AdditionalInfo',
          },
        },
      ],
      colConfig: 'SINGLE_COL',
    });
  },

  createSPRequestModel: (reason: TREQUEST_RAISE_SPAREPART_REQUEST['reason']): TLiftedFormModel => {
    return PIPELINE_FORM.sanitize({
      inputs: [
        {
          input: {
            type: 'SEARCH',
            searchOnSearchTextClick: true,
          },
          metadata: {
            keyName: 'current_name',
            label: 'tm_raise_request.CurrentSPName',
            isRequired: true,
          },
        },
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
          },
          metadata: {
            keyName: 'current_specification',
            label: 'tm_raise_request.CurrentSPSpec',
            isDisabled: true,
            dependsOn: {
              keyName: 'current_name',
            },
          },
        },
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
          },
          metadata: {
            keyName: 'current_brand',
            label: 'tm_raise_request.Brand',
            isRequired: true,
            isDisabled: true,
            dependsOn: {
              keyName: 'current_specification',
              valuesCallback: function (dependsOnValue: TInputTypes['value'], currentInputValue: TInputTypes['value']) {
                const selectedValue = dependsOnValue as string[];
                const currentValue = currentInputValue as string[];
                return {
                  updatedValues: {
                    value: selectedValue.length === 0 ? selectedValue : currentValue,
                    type: 'DROPDOWN_WITH_SEARCH',
                  },
                };
              },
            },
          },
        },
        {
          input: {
            type: 'CHECKBOX',
            checked: false,
            disabled: false,
            indeterminate: false,
          },
          metadata: {
            keyName: 'same_as_current',
            label: 'tm_raise_request.SameAsCurrentSP',
            displayInputInline: true,
            checkboxFirst: true,
          },
        },
        {
          input: {
            type: 'SEARCH',
            searchOnSearchTextClick: true,
          },
          metadata: {
            keyName: 'requested_name',
            label: 'tm_raise_request.RequestedSPName',
            isRequired: true,
            isDisabled: true,
            dependsOn: {
              keyName: 'current_name',
            },
          },
        },
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
          },
          metadata: {
            keyName: 'requested_specification',
            label: 'tm_raise_request.RequestedSPSpec',
            isDisabled: true,
            dependsOn: {
              keyName: 'requested_name',
            },
          },
        },
        {
          input: {
            type: 'DROPDOWN_WITH_SEARCH',
          },
          metadata: {
            keyName: 'requested_brand',
            label: 'tm_raise_request.Brand',
            isDisabled: true,
            isRequired: true,
            dependsOn: {
              keyName: 'requested_specification',
              valuesCallback: function (dependsOnValue: TInputTypes['value'], currentInputValue: TInputTypes['value']) {
                const selectedValue = dependsOnValue as string[];
                const currentValue = currentInputValue as string[];
                return {
                  updatedValues: {
                    value: selectedValue.length === 0 ? selectedValue : currentValue,
                    type: 'DROPDOWN_WITH_SEARCH',
                  },
                };
              },
            },
          },
        },
        {
          input: {
            type: 'INPUT',
            subType: 'number',
            min: 1,
          },
          metadata: {
            keyName: 'quantity',
            label:
              reason === 'MISSING'
                ? 'tm_raise_request.MissingSparepartQuantity'
                : 'tm_raise_request.ReturnedSparepartQuantity',
            isRequired: true,
            isDisabled: true,
            dependsOn: {
              keyName: 'requested_name',
            },
          },
        },
        ...(reason === 'BROKEN'
          ? [
              {
                input: {
                  type: 'INPUT' as const,
                  subType: 'number' as const,
                  min: 0,
                },
                metadata: {
                  keyName: 'broken_quantity',
                  label: 'tm_raise_request.BrokenSparepartQuantity',
                  isDisabled: true,
                  dependsOn: {
                    keyName: 'requested_name',
                  },
                },
              },
            ]
          : []),
        {
          input: {
            type: 'INPUT',
            subType: 'number',
          },
          metadata: {
            keyName: 'requested_in_stock_quantity',
            label: 'tm_raise_request.InStockQuantity',
            isDisabled: true,
            removeFromPayload: true,
          },
        },
      ],
      colConfig: 'SINGLE_COL',
    });
  },

  identity: (): TLiftedFormModel => {
    return {
      inputs: [],
    };
  },

  /**
   * Updates the `subReasonOpt` field in the `reasonForm` based on the selected reason
   * and the corresponding sub-reasons from the `reasonMap`.
   *
   * If sub-reasons are available for the selected reason, it adds them as options to the dropdown.
   * It also calls updateSubReasonField() to update the visibility and requirements of the `subReasonOpt` field.
   *
   * @param reasonForm - The form model containing the current reason and inputs.
   * @param reasonMap - A map containing available reasons and their corresponding sub-reasons.
   * @param selectedReason - The currently selected reason string.
   * @returns A new form model with updated sub-reason field settings and dropdown options.
   */
  updateSubReasonsOnReasonSelection(
    reasonForm: TLiftedFormModel,
    reasonMap: GET_SPAREPART_REQUEST_REASONS,
    selectedReason: string,
  ): TLiftedFormModel {
    const subReason = reasonForm.payload?.['subReasonOpt'];
    if (selectedReason && reasonMap) {
      const matchingReason = reasonMap.reasonList.find((x) => x.reason === selectedReason);
      const subReasons: string[] = matchingReason?.subReasons ? matchingReason.subReasons : [];
      const currentsubReasonInputIndex = reasonForm.inputs.findIndex((x) => x.metadata.keyName === 'subReasonOpt');
      if (subReasons.length) {
        const options = subReasons.map((x) => {
          return { value: x };
        });
        if (!subReason)
          reasonForm = {
            ...RAISE_SPAREPART_REQUEST_FORM_MODEL.updateDropdownForKey('subReasonOpt', reasonForm, options),
          };
        return this.updateSubReasonField(reasonForm, false, currentsubReasonInputIndex);
      } else {
        return this.updateSubReasonField(reasonForm, true, currentsubReasonInputIndex);
      }
    }
    return reasonForm;
  },

  /**
   * Updates the visibility, requirement, and payload inclusion of the sub-reason input field.
   *
   * When `show` is true, the field will be hidden and removed from the payload.
   * When `show` is false, the field will be visible and required.
   *
   * @param reasonForm - The lifted form model to be updated.
   * @param show - Whether to hide and remove the field from payload (`true`) or show and require it (`false`).
   * @param subReasonIndex - Index of the sub-reason input field in the form model.
   * @returns A new form model with the updated input.
   */
  updateSubReasonField(reasonForm: TLiftedFormModel, show: boolean, subReasonIndex: number): TLiftedFormModel {
    const updatedInput = {
      ...reasonForm.inputs[subReasonIndex],
      metadata: {
        ...reasonForm.inputs[subReasonIndex]?.metadata,
        isHidden: show,
        removeFromPayload: show,
        isRequired: !show,
      },
    };
    return RAISE_SPAREPART_REQUEST_FORM_MODEL.updateInput(reasonForm, updatedInput, subReasonIndex);
  },

  /**
   * Validates whether the modal data is complete and free of errors.
   *
   * Checks for the following:
   * - The reason field is valid.
   * - All spare part forms are valid.
   * - There are no duplicate spare part forms (only for 'REPLACE' reason).
   * - At least one spare part form is present.
   *
   * @param reasonIsValid - Indicates whether the reason field is valid.
   * @param sparepartRequestForms - An array of spare part form wrappers to be validated.
   * @param reason - The selected reason string, used to check for duplicates.
   * @returns A tuple: [isValid, errorString]
   *   - isValid: true if all conditions are met, false otherwise.
   *   - errorString: corresponding error translation key.
   */
  validateModalData(reasonIsValid: boolean, sparepartRequestForms: TSPForm[], reason?: string): [boolean, string] {
    const isDataInValid = !reasonIsValid || sparepartRequestForms.some((form) => !form.form.isValid);
    const duplicateForm = sparepartRequestForms.some((form) => this.checkDuplicateSP(form.form, reason));
    if (isDataInValid) {
      return [false, 'tm_raise_request.FixAboveErr'];
    } else if (duplicateForm) {
      return [false, 'tm_raise_request.DuplicateSPError'];
    } else if (sparepartRequestForms.length <= 0) {
      return [false, 'tm_raise_request.AtleastOneCurrent&RequestedSPErr'];
    } else return [true, ''];
  },

  /**
   * Checks whether a spare part request form is a duplicate.
   *
   * A form is considered a duplicate if:
   * - The reason is 'REPLACE', and
   * - The current and requested - name, specification, and brand are all identical.
   *
   * @param form - The lifted form model representing a spare part request.
   * @param reason - The selected reason string, used to apply duplicate-check logic.
   * @returns `true` if the form is a duplicate, `false` otherwise.
   */
  checkDuplicateSP(form: TLiftedFormModel, reason?: string) {
    if (
      reason === 'REPLACE' &&
      form.payload?.[SPAREPART_REQUEST_MODAL_KEYS.CURRENT_NAME] ===
        form.payload?.[SPAREPART_REQUEST_MODAL_KEYS.REQUESTED_NAME] &&
      form.payload?.[SPAREPART_REQUEST_MODAL_KEYS.CURRENT_SPECIFICATION] ===
        form.payload?.[SPAREPART_REQUEST_MODAL_KEYS.REQUESTED_SPECIFICATION] &&
      form.payload?.[SPAREPART_REQUEST_MODAL_KEYS.CURRENT_BRAND] ===
        form.payload?.[SPAREPART_REQUEST_MODAL_KEYS.REQUESTED_BRAND]
    )
      return true;
    return false;
  },

  /**
   * Syncs the requested spare part fields with the current spare part fields when the
   * "Same as Current" checkbox is checked, or resets them when unchecked.
   *
   * @param form - The current lifted form model.
   * @param isChecked - Whether the checkbox is checked.
   * @param currSpBrandsWithSpecifications - Current spare part brand/specification data used to populate requested dropdowns.
   * @returns Updated form model with synced or reset requested fields.
   */
  syncRequestedWithCurrent(
    form: TLiftedFormModel,
    isChecked: boolean,
    currSpBrandsWithSpecifications: TRESPONSE_GET_ALL_BRANDS_AND_SPECIFICATIONS['brandsWithSpecifications'],
  ): TLiftedFormModel {
    const payload = form.payload as TSP_REQUEST_FORM_PAYLOAD | undefined;

    const specifications = currSpBrandsWithSpecifications
      .flatMap((v) => v.specifications)
      .flatMap((spec) => (spec.specificationOpt ? [spec.specificationOpt] : []));
    const specOptions = [...new Set(specifications)].map((v) => ({
      value: v,
    }));
    const brandOptions = [...new Set(currSpBrandsWithSpecifications.map((v) => v.brand))].map((v) => ({ value: v }));

    let updatedForm = form;

    const requestedNameIdx = updatedForm.inputs.findIndex(
      (inp) => inp.metadata.keyName === SPAREPART_REQUEST_MODAL_KEYS.REQUESTED_NAME,
    );
    if (requestedNameIdx !== -1) {
      const nameInput = updatedForm.inputs[requestedNameIdx];
      if (nameInput.input.type === 'SEARCH') {
        updatedForm = FORM_MODIFIER.updateInputByIndex(
          updatedForm,
          {
            ...nameInput,
            input: {
              ...nameInput.input,
              value: isChecked ? payload?.current_name : undefined,
              searchText: undefined,
              showSuggestions: false,
            },
            metadata: { ...nameInput.metadata },
          },
          requestedNameIdx,
        );
      }
    }

    const requestedSpecIdx = updatedForm.inputs.findIndex(
      (inp) => inp.metadata.keyName === SPAREPART_REQUEST_MODAL_KEYS.REQUESTED_SPECIFICATION,
    );
    if (requestedSpecIdx !== -1) {
      const specInput = updatedForm.inputs[requestedSpecIdx];
      if (specInput.input.type === 'DROPDOWN_WITH_SEARCH') {
        updatedForm = FORM_MODIFIER.updateInputByIndex(
          updatedForm,
          {
            ...specInput,
            input: {
              ...specInput.input,
              value: isChecked ? payload?.current_specification : undefined,
              options: isChecked ? specOptions : [],
            },
            metadata: { ...specInput.metadata, isDisabled: !isChecked || !specOptions.length },
          },
          requestedSpecIdx,
        );
      }
    }

    const requestedBrandIdx = updatedForm.inputs.findIndex(
      (inp) => inp.metadata.keyName === SPAREPART_REQUEST_MODAL_KEYS.REQUESTED_BRAND,
    );
    if (requestedBrandIdx !== -1) {
      const brandInput = updatedForm.inputs[requestedBrandIdx];
      if (brandInput.input.type === 'DROPDOWN_WITH_SEARCH') {
        updatedForm = FORM_MODIFIER.updateInputByIndex(
          updatedForm,
          {
            ...brandInput,
            input: {
              ...brandInput.input,
              value: isChecked ? payload?.current_brand : undefined,
              options: isChecked ? brandOptions : [],
            },
            metadata: { ...brandInput.metadata, isDisabled: !isChecked || !brandOptions.length },
          },
          requestedBrandIdx,
        );
      }
    }

    return updatedForm;
  },

  updateDropdownForKey: (
    key: string,
    liftedFormModel: TLiftedFormModel,
    results: TDropdownItem[],
  ): TLiftedFormModel => {
    return FORM_MODIFIER.updateDropdownForKey(key, liftedFormModel, results);
  },

  updateInStockQuantity(form: TLiftedFormModel, quantity: number | undefined): TLiftedFormModel {
    const index = form.inputs.findIndex(
      (inp) => inp.metadata.keyName === SPAREPART_REQUEST_MODAL_KEYS.REQUESTED_IN_STOCK_QUANTITY,
    );
    const input = form.inputs[index];
    if (index === -1 || input.input.type !== 'INPUT') return form;

    return FORM_MODIFIER.updateInputByIndex(form, { ...input, input: { ...input.input, value: quantity } }, index);
  },

  onParentSave: (formModel: TLiftedFormModel): TLiftedFormModel => {
    return FORM_MODIFIER.markTouchedInputs(formModel);
  },

  updateInput(prevLiftedFormModel: TLiftedFormModel, updatedInput: TLiftedFormModel['inputs'][number], index: number) {
    return FORM_MODIFIER.updateInputByIndex(prevLiftedFormModel, updatedInput, index);
  },
};
